import { JwtService } from 'api';
import { isDefinitiveAuthRejection } from './authError';
import {
  clearOtpVerified,
  clearStoredAuthTokens,
  getStoredRefreshToken,
  setStoredAccessToken,
} from './authStorage';

export type TokenRefreshResult =
  | { kind: 'success'; access: string }
  | { kind: 'cleared' }
  | { kind: 'transient' };

let refreshInFlight: Promise<TokenRefreshResult> | null = null;

const clearExpiredAuthSession = () => {
  clearOtpVerified();
  clearStoredAuthTokens();
};

const executeTokenRefresh = async (): Promise<TokenRefreshResult> => {
  const refresh = getStoredRefreshToken();

  if (!refresh) {
    clearExpiredAuthSession();
    return { kind: 'cleared' };
  }

  try {
    const response = await JwtService.authJwtRefreshCreate({ refresh });

    if (response?.access) {
      setStoredAccessToken(response.access);
      return { kind: 'success', access: response.access };
    }

    // A successful HTTP response without an access token is unusable; treat it as expired.
    clearExpiredAuthSession();
    return { kind: 'cleared' };
  } catch (error) {
    // Only wipe the session when the refresh token is definitively rejected.
    // Network blips and 5xx must leave stored tokens intact so the next retry can recover.
    if (isDefinitiveAuthRejection(error)) {
      clearExpiredAuthSession();
      return { kind: 'cleared' };
    }

    return { kind: 'transient' };
  }
};

/**
 * Issues at most one /auth/jwt/refresh request at a time.
 * Concurrent callers share the in-flight promise and receive the same result.
 *
 * Returns a discriminated `TokenRefreshResult`:
 *  - `success`   — new access token available
 *  - `cleared`   — session was definitively wiped (401/403 or missing refresh token)
 *  - `transient` — network/5xx error; tokens are intact and a retry is safe
 */
export const singleFlightTokenRefresh = (): Promise<TokenRefreshResult> => {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = executeTokenRefresh().finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
};
